import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";

import SEOHead from "../components/common/SEOHead";
import farProducts from "../data/fustellificioFarProducts";
import "./styles/fustellificioFar.css";

const formatPrice = (value) =>
	new Intl.NumberFormat("it-IT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 0,
	}).format(value);

const cartKey = "fustellificio-far-cart";

const readStoredCart = () => {
	try {
		const stored = window.localStorage.getItem(cartKey);
		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
};

const FustellificioFar = () => {
	const { slug } = useParams();
	const [cart, setCart] = useState(readStoredCart);
	const [cartOpen, setCartOpen] = useState(false);
	const [checkoutOpen, setCheckoutOpen] = useState(false);
	const [orderSent, setOrderSent] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState("Tutte");
	const [cartBump, setCartBump] = useState(false);
	const feedRef = useRef(null);

	const selectedProduct = farProducts.find((product) => product.slug === slug);
	const feedProducts = selectedProduct
		? [
				selectedProduct,
				...farProducts.filter((product) => product.slug !== selectedProduct.slug),
		  ]
		: farProducts;
	const categories = useMemo(
		() => ["Tutte", ...new Set(farProducts.map((product) => product.category))],
		[]
	);
	const visibleProducts =
		selectedCategory === "Tutte"
			? farProducts
			: farProducts.filter((product) => product.category === selectedCategory);

	const cartLines = cart
		.map((item) => ({
			...item,
			product: farProducts.find((product) => product.slug === item.slug),
		}))
		.filter((item) => item.product);
	const cartTotal = cartLines.reduce(
		(total, item) => total + item.product.price * item.quantity,
		0
	);
	const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [slug]);

	useEffect(() => {
		window.localStorage.setItem(cartKey, JSON.stringify(cart));
	}, [cart]);

	useEffect(() => {
		const feed = feedRef.current;
		if (!selectedProduct || !feed) {
			return undefined;
		}

		let locked = false;
		let touchStartY = 0;

		const goToSlide = (direction) => {
			const slides = Array.from(feed.querySelectorAll(".far-feed-slide"));
			if (slides.length === 0 || locked) {
				return;
			}

			const currentIndex = slides.reduce((closestIndex, slide, index) => {
				const currentDistance = Math.abs(slide.offsetTop - feed.scrollTop);
				const closestDistance = Math.abs(slides[closestIndex].offsetTop - feed.scrollTop);
				return currentDistance < closestDistance ? index : closestIndex;
			}, 0);
			const nextIndex = Math.min(
				Math.max(currentIndex + direction, 0),
				slides.length - 1
			);

			if (nextIndex === currentIndex) {
				return;
			}

			locked = true;
			feed.scrollTo({
				top: slides[nextIndex].offsetTop,
				behavior: "smooth",
			});
			window.setTimeout(() => {
				locked = false;
			}, 680);
		};

		const handleWheel = (event) => {
			event.preventDefault();
			goToSlide(event.deltaY > 0 ? 1 : -1);
		};

		const handleTouchStart = (event) => {
			touchStartY = event.touches[0].clientY;
		};

		const handleTouchEnd = (event) => {
			const delta = touchStartY - event.changedTouches[0].clientY;
			if (Math.abs(delta) > 44) {
				goToSlide(delta > 0 ? 1 : -1);
			}
		};

		feed.addEventListener("wheel", handleWheel, { passive: false });
		feed.addEventListener("touchstart", handleTouchStart, { passive: true });
		feed.addEventListener("touchend", handleTouchEnd, { passive: true });

		return () => {
			feed.removeEventListener("wheel", handleWheel);
			feed.removeEventListener("touchstart", handleTouchStart);
			feed.removeEventListener("touchend", handleTouchEnd);
		};
	}, [selectedProduct]);

	const addToCart = (product, quantity = 1) => {
		setCart((currentCart) => {
			const existing = currentCart.find((item) => item.slug === product.slug);
			if (existing) {
				return currentCart.map((item) =>
					item.slug === product.slug
						? { ...item, quantity: item.quantity + quantity }
						: item
				);
			}
			return [...currentCart, { slug: product.slug, quantity }];
		});
		setCartBump(true);
		window.setTimeout(() => setCartBump(false), 420);
		setCartOpen(true);
	};

	const changeQuantity = (slugToUpdate, quantity) => {
		setCart((currentCart) =>
			currentCart
				.map((item) =>
					item.slug === slugToUpdate ? { ...item, quantity } : item
				)
				.filter((item) => item.quantity > 0)
		);
	};

	const submitOrder = (event) => {
		event.preventDefault();
		setOrderSent(true);
	};

	const ProductCard = ({ product }) => (
		<article className="far-product-card">
			<Link to={`/fustellificio-far/prodotto/${product.slug}`}>
				<div className="far-product-media">
					<img src={product.image} alt={product.name} />
				</div>
				<div className="far-product-card-body">
					<div>
						<span>{product.category}</span>
						<h3>{product.name}</h3>
					</div>
					<p>{product.description}</p>
					<div className="far-product-card-bottom">
						<strong>{formatPrice(product.price)}</strong>
						<small>{product.leadTime}</small>
					</div>
				</div>
			</Link>
			<button type="button" onClick={() => addToCart(product)}>
				Aggiungi
			</button>
		</article>
	);

	const CartPanel = () => (
		<aside className={`far-cart ${cartOpen ? "is-open" : ""}`} aria-live="polite">
			<div className="far-cart-head">
				<div>
					<span>Carrello</span>
					<strong>{cartCount} articoli</strong>
				</div>
				<button type="button" onClick={() => setCartOpen(false)}>
					Chiudi
				</button>
			</div>
			{cartLines.length === 0 ? (
				<div className="far-cart-empty">Aggiungi una fustella per preparare l'ordine.</div>
			) : (
				<React.Fragment>
					<div className="far-cart-lines">
						{cartLines.map(({ product, quantity }) => (
							<div className="far-cart-line" key={product.slug}>
								<img src={product.image} alt="" />
								<div>
									<strong>{product.name}</strong>
									<span>{formatPrice(product.price)}</span>
								</div>
								<div className="far-stepper">
									<button
										type="button"
										onClick={() => changeQuantity(product.slug, quantity - 1)}
									>
										-
									</button>
									<span>{quantity}</span>
									<button
										type="button"
										onClick={() => changeQuantity(product.slug, quantity + 1)}
									>
										+
									</button>
								</div>
							</div>
						))}
					</div>
					<div className="far-cart-total">
						<span>Totale stimato</span>
						<strong>{formatPrice(cartTotal)}</strong>
					</div>
					<button
						type="button"
						className="far-primary-btn"
						onClick={() => {
							setCheckoutOpen(true);
							setCartOpen(false);
						}}
					>
						Vai al checkout
					</button>
				</React.Fragment>
			)}
		</aside>
	);

	return (
		<React.Fragment>
			<SEOHead
				title={selectedProduct ? selectedProduct.name : "Fustellificio Rosselli Ecommerce Fustelle"}
				description={
					selectedProduct
						? selectedProduct.description
						: "Fustellificio Rosselli: ecommerce per fustelle tecniche, packaging industriale, video prodotto, carrello e richiesta ordine B2B."
				}
				path={selectedProduct ? `/fustellificio-far/prodotto/${selectedProduct.slug}` : "/fustellificio-far"}
				image={selectedProduct ? selectedProduct.image : "/fustellificio-far/products/07e94932-3cd3-4724-8bdc-52e58add2293.jpg"}
				noindex={Boolean(selectedProduct)}
				jsonLd={[
					{
						"@context": "https://schema.org",
						"@type": "WebPage",
						"@id": selectedProduct
							? `https://www.northwestwind.org/fustellificio-far/prodotto/${selectedProduct.slug}#webpage`
							: "https://www.northwestwind.org/fustellificio-far#webpage",
						url: selectedProduct
							? `https://www.northwestwind.org/fustellificio-far/prodotto/${selectedProduct.slug}`
							: "https://www.northwestwind.org/fustellificio-far",
						name: selectedProduct
							? selectedProduct.name
							: "Fustellificio Rosselli Ecommerce Fustelle",
						description: selectedProduct
							? selectedProduct.description
							: "Ecommerce per fustelle tecniche e packaging industriale.",
						isPartOf: { "@id": "https://www.northwestwind.org/#website" },
					},
				]}
			/>

			<div className="far-page">
				<header className="far-header">
					<Link className="far-brand" to="/fustellificio-far" aria-label="Fustellificio FAR home">
						<span className="far-brand-full">Fustellificio Artigiano Rosselli</span>
						<span className="far-brand-short">Fustellificio Rosselli</span>
					</Link>
					<nav>
						<a href="#catalogo">Catalogo</a>
						<a href="#reel">Video</a>
						<a href="#checkout">Checkout</a>
					</nav>
					<button
						type="button"
						className={`far-cart-button ${cartCount > 0 ? "is-filled" : "is-empty"} ${cartBump ? "is-bumping" : ""}`}
						aria-label={`Apri carrello, ${cartCount} articoli`}
						onClick={() => setCartOpen(true)}
					>
						<FontAwesomeIcon icon={faCartShopping} className="far-cart-icon" aria-hidden="true" />
						<span>{cartCount}</span>
					</button>
				</header>
				<button
					type="button"
					className={`far-mobile-cart-float ${cartCount > 0 ? "is-filled" : "is-empty"} ${cartBump ? "is-bumping" : ""}`}
					aria-label={`Apri carrello, ${cartCount} articoli`}
					onClick={() => setCartOpen(true)}
				>
					<FontAwesomeIcon icon={faCartShopping} className="far-cart-icon" aria-hidden="true" />
					<span>{cartCount}</span>
				</button>

				{!selectedProduct && (
					<main>
						<section className="far-hero">
							<div className="far-hero-copy">
								<p className="far-kicker">Fustelle online per packaging industriale</p>
								<h1>Taglio tecnico, produzione rapida</h1>
								<p>
									Ordina fustelle tecniche online, consegna in 48 ore
								</p>
								<div className="far-hero-actions">
									<a href="#catalogo" className="far-primary-btn">Acquista</a>
									<a href="#reel" className="far-secondary-btn">Video</a>
								</div>
							</div>
							<div className="far-hero-video" aria-label="Video lavorazione fustella">
								<video
									src="/fustellificio-far/video/fustello-video.mp4"
									autoPlay
									muted
									loop
									playsInline
								/>
								<div className="far-video-veil">
									<span>Preview officina</span>
									<strong>FAR</strong>
								</div>
							</div>
						</section>

						<section className="far-industrial-strip" aria-label="Servizi principali">
							<div><span>01</span> Preventivo immediato</div>
							<div><span>02</span> File tecnico pronto</div>
							<div><span>03</span> Produzione e consegna</div>
						</section>

						<section className="far-catalog" id="catalogo">
							<div className="far-section-head">
								<div>
									<p className="far-kicker">Shop tecnico</p>
									<h2>Catalogo fustelle</h2>
								</div>
								<div className="far-filter-row" aria-label="Filtra prodotti">
									{categories.map((category) => (
										<button
											type="button"
											key={category}
											className={selectedCategory === category ? "is-active" : ""}
											onClick={() => setSelectedCategory(category)}
										>
											{category}
										</button>
									))}
								</div>
							</div>
							<div className="far-products-grid">
								{visibleProducts.map((product) => (
									<ProductCard product={product} key={product.slug} />
								))}
							</div>
						</section>

						<section className="far-reel-section" id="reel">
							<div className="far-reel-copy">
								<p className="far-kicker">Presentazioni verticali</p>
								<h2>Una vetrina social pronta per molti video prodotto.</h2>
								<p>
									Una corsia verticale per vedere materiali, dettagli di taglio,
									montaggi, prima e dopo. Ogni video accompagna il prodotto nel momento
									in cui il cliente sta scegliendo.
								</p>
							</div>
							<div className="far-reel-rail">
								{[
									"/fustellificio-far/video/presentazione-fustello-1.mp4",
									"/fustellificio-far/video/presentazione-fustello-2.mp4",
								].map((video, index) => (
									<div className="far-reel-phone" key={video}>
										<video src={video} autoPlay muted loop playsInline />
										<div className="far-reel-caption">
											<span>Reel {String(index + 1).padStart(2, "0")}</span>
											<strong>Dettaglio fustello</strong>
										</div>
									</div>
								))}
								<div className="far-reel-placeholder">
									<span>+</span>
									<strong>Prossimo video</strong>
								</div>
							</div>
						</section>
					</main>
				)}

				{selectedProduct && (
					<main className="far-product-page far-feed-page">
						<Link to="/fustellificio-far" className="far-back-link">Torna al catalogo</Link>
						<section className="far-product-feed" ref={feedRef} aria-label="Feed prodotti">
							{feedProducts.map((product, index) => (
								<article className="far-feed-slide" key={product.slug}>
									<div className="far-feed-media">
										<img src={product.image} alt={product.name} />
										<div className="far-feed-index">
											{String(index + 1).padStart(2, "0")} / {String(feedProducts.length).padStart(2, "0")}
										</div>
									</div>
									<div className="far-feed-copy">
										<p className="far-kicker">{product.category}</p>
										<h1>{product.name}</h1>
										<p>{product.description}</p>
										<div className="far-detail-price">
											<strong>{formatPrice(product.price)}</strong>
											<span>{product.leadTime}</span>
										</div>
										<ul>
											{product.specs.map((spec) => (
												<li key={spec}>{spec}</li>
											))}
										</ul>
										<div className="far-detail-actions">
											<button
												type="button"
												className="far-primary-btn"
												onClick={() => addToCart(product)}
											>
												Aggiungi al carrello
											</button>
											<button
												type="button"
												className="far-secondary-btn"
												onClick={() => {
													addToCart(product);
													setCheckoutOpen(true);
												}}
											>
												Richiedi ora
											</button>
										</div>
								</div>
								</article>
							))}
						</section>
					</main>
				)}

				<section className={`far-checkout ${checkoutOpen ? "is-open" : ""}`} id="checkout">
					<div className="far-checkout-card">
						<div className="far-checkout-head">
							<div>
								<p className="far-kicker">Checkout B2B</p>
								<h2>Richiesta ordine</h2>
							</div>
							<button type="button" onClick={() => setCheckoutOpen(false)}>Chiudi</button>
						</div>
						{orderSent ? (
							<div className="far-order-success">
								<strong>Richiesta inviata.</strong>
								<p>
									Il team FAR ricevera riepilogo prodotti, dati aziendali e note
									tecniche per confermare tempi, lavorazioni e consegna.
								</p>
							</div>
						) : (
							<form onSubmit={submitOrder}>
								<div className="far-checkout-grid">
									<label>
										Azienda
										<input required placeholder="Rossi Packaging Srl" />
									</label>
									<label>
										Email
										<input required type="email" placeholder="acquisti@azienda.it" />
									</label>
									<label>
										Telefono
										<input required placeholder="+39 02 0000 0000" />
									</label>
									<label>
										Consegna
										<select defaultValue="standard">
											<option value="standard">Standard 5-7 giorni</option>
											<option value="rapida">Rapida 48/72h</option>
											<option value="ritiro">Ritiro in sede</option>
										</select>
									</label>
									<label className="far-checkout-notes">
										Note tecniche
										<textarea placeholder="Materiale, spessore, macchina, urgenze..." />
									</label>
								</div>
								<div className="far-checkout-summary">
									<span>Totale stimato</span>
									<strong>{formatPrice(cartTotal)}</strong>
								</div>
								<button type="submit" className="far-primary-btn">
									Conferma richiesta
								</button>
							</form>
						)}
					</div>
				</section>

				<CartPanel />
			</div>
		</React.Fragment>
	);
};

export default FustellificioFar;
